import React, { useState, useEffect } from "react";
import { Icon, Button, Text } from "@amzn/meridian";
import plusTokens from "@amzn/meridian-tokens/base/icon/plus";
import minusTokens from "@amzn/meridian-tokens/base/icon/minus";
import "./ZoomControl.scss";

const ZOOM_STEP = 0.3;

type ZoomControlProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
};

const ZoomControl = ({ videoRef }: ZoomControlProps) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1 });
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkZoomSupport = async () => {
      try {
        if (!videoRef.current?.srcObject) {
          setIsChecking(false);
          return;
        }

        const stream = videoRef.current.srcObject as MediaStream;
        const videoTrack = stream.getVideoTracks()[0];

        if (!videoTrack) {
          setZoomSupported(false);
          setIsChecking(false);
          return;
        }

        const capabilities = videoTrack.getCapabilities();

        if ('zoom' in capabilities && capabilities.zoom) {
          const zoomCap = capabilities.zoom as any;
          setZoomSupported(true);
          setZoomRange({
            min: zoomCap.min || 1,
            max: Math.min(zoomCap.max || 10, 4.0),
          });

          const settings = videoTrack.getSettings();
          if ('zoom' in settings && settings.zoom) {
            setZoomLevel(settings.zoom as number);
          }
        } else {
          setZoomSupported(false);
        }

        setIsChecking(false);
      } catch (error) {
        console.warn("Failed to check zoom support:", error);
        setZoomSupported(false);
        setIsChecking(false);
      }
    };

    const timeout = setTimeout(checkZoomSupport, 500);
    return () => clearTimeout(timeout);
  }, [videoRef.current?.srcObject]);

  const applyZoom = async (newZoomLevel: number) => {
    try {
      if (!videoRef.current?.srcObject) {
        return;
      }

      const stream = videoRef.current.srcObject as MediaStream;
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        return;
      }

      const clamped = Math.max(zoomRange.min, Math.min(zoomRange.max, newZoomLevel));

      await videoTrack.applyConstraints({
        // @ts-ignore - zoom is not in TypeScript's MediaTrackConstraints type yet
        advanced: [{ zoom: clamped }]
      });

      setZoomLevel(clamped);
    } catch (error) {
      console.error("Failed to apply zoom:", error);
    }
  };

  const handleZoomIn = () => applyZoom(zoomLevel + ZOOM_STEP);
  const handleZoomOut = () => applyZoom(zoomLevel - ZOOM_STEP);

  if (isChecking || !zoomSupported) {
    return null;
  }

  return (
    <div className="zoom-control-container">
        <Button
          onClick={handleZoomOut}
          disabled={zoomLevel <= zoomRange.min}
          className="zoom-minus-button"
          aria-label="Zoom out"
          circular={true}
          type="icon"
          size="small"
        >
          <Icon color="inverted" tokens={minusTokens}/>
        </Button>

        <Text color="inverted" type="b200" className="zoom-level-display">
          {zoomLevel.toFixed(1)}x
        </Text>

        <Button
          onClick={handleZoomIn}
          disabled={zoomLevel >= zoomRange.max}
          className="zoom-plus-button"
          circular={true}
          type="icon"
          aria-label="Zoom in"
          size="small"
        >
          <Icon color="inverted" tokens={plusTokens}/>
        </Button>
    </div>
  );
};

export default ZoomControl;
